/**
 * @file Simulation hors-ligne ANALYTIQUE et deterministe (CombatTime / RegularKillRate de l'original) : farm du niveau courant pendant l'absence.
 */
import { Decimal } from '@shared/bignum';
import type { BalanceConfig, TargetDef } from '@config/types';
import { stageHp, stageMoney } from './formulas';

export interface OfflineInput {
  bal: BalanceConfig;
  /** Secondes d'absence reelles. */
  awaySeconds: number;
  /** Plafond (heures) = stat offlineHours. */
  capHours: number;
  /** Efficacite = stat offlineRate (0.5 de base). */
  efficiency: number;
  /** DPS total de l'equipe (WeaponStatsCache.teamDps, crit en esperance). */
  teamDps: Decimal;
  /** Multiplicateur d'argent final (stat money, hors buffs : les buffs ne comptent pas hors-ligne). */
  moneyMult: Decimal;
  /** Niveau farme (run.stageValue). */
  stageValue: number;
  /** Table d'arbres du biome courant (pour moyenner PV et recompenses, raretes incluses). */
  treeTable: Array<{ def: TargetDef; weight: number }>;
}

export interface OfflineOutput {
  simulatedSeconds: number;
  capped: boolean;
  kills: number;
  money: Decimal;
}

export const EMPTY_OFFLINE: OfflineOutput = { simulatedSeconds: 0, capped: false, kills: 0, money: new Decimal(0) };

/**
 * Aucun aleatoire : moyennes ponderees.
 *  simulated = min(away, capHours x 3600) ; arbre moyen : PV = stageHp x moyenne(w x hpMult), argent = stageMoney x moyenne(w x rewardMult) ;
 *  temps par kill = PV / dps + travelOverheadSeconds ; kills = floor(simulated x efficiency / tempsParKill) ; argent = moyen x kills x moneyMult.
 */
export const simulateOffline = (input: OfflineInput): OfflineOutput => {
  const cap = Math.max(0, input.capHours) * 3600;
  const away = Math.max(0, input.awaySeconds);
  const simulatedSeconds = Math.min(away, cap);
  const capped = away > cap;
  let wSum = 0;
  let hpW = 0;
  let rewardW = 0;
  for (const e of input.treeTable) {
    wSum += e.weight;
    hpW += e.weight * e.def.hpMult;
    rewardW += e.weight * e.def.rewardMult;
  }
  if (wSum <= 0 || input.teamDps.lte(0) || simulatedSeconds <= 0) return { simulatedSeconds, capped, kills: 0, money: new Decimal(0) };
  const hpAvg = stageHp(input.bal, input.stageValue).times(hpW / wSum);
  const moneyAvg = stageMoney(input.bal, input.stageValue).times(rewardW / wSum);
  const timePerKill = hpAvg.div(input.teamDps).toNumber() + input.bal.offline.travelOverheadSeconds;
  const kills = Math.floor((simulatedSeconds * input.efficiency) / Math.max(1e-6, timePerKill));
  const money = moneyAvg.times(kills).times(input.moneyMult);
  return { simulatedSeconds, capped, kills, money };
};
